import { useMemo, useState } from "react";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Building2,
  Clock,
  Layers,
  Loader2,
  Rows3,
  Search,
  Upload,
} from "lucide-react";
import type { CfoFundingLine, CfoFundingReceipt } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import {
  MATCH,
  diagnosticLabel,
  matchOf,
  reasonLabel,
  usd,
  websiteOf,
  type Match,
} from "./fundingLabels";

const PAGE = 25;
type Filter = "all" | Match | "warnings";

function Stat({
  icon: Icon,
  label,
  value,
  hint,
  tone = "slate",
}: {
  icon: typeof Rows3;
  label: string;
  value: number | string;
  hint?: string;
  tone?: "slate" | "amber";
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-slate-500">
        <Icon className={cn("h-3.5 w-3.5", tone === "amber" ? "text-amber-500" : "text-slate-400")} />
        {label}
      </p>
      <p className="mt-1 text-xl font-semibold tabular-nums text-slate-900">{value}</p>
      {hint && <p className="mt-0.5 truncate text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}

/** Step 2: the rows of the file as they will be recorded, before anything is. */
export function FundingPreviewStep({
  receipt,
  lines,
  canConfirm,
  busy,
  error,
  onBack,
  onConfirm,
}: {
  receipt: CfoFundingReceipt;
  lines: CfoFundingLine[] | null;
  canConfirm: boolean;
  busy: boolean;
  error: string | null;
  onBack: () => void;
  onConfirm: () => void;
}) {
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);

  const rows = useMemo(() => lines ?? [], [lines]);
  const counts = useMemo(() => {
    const byMatch = { new: new Set<string>(), known: new Set<string>(), check: new Set<string>() };
    let warnings = 0;
    for (const line of rows) {
      byMatch[matchOf(line)].add(line.group_key);
      if (line.diagnostics.length) warnings += 1;
    }
    return {
      all: rows.length,
      newCompanies: byMatch.new.size,
      known: byMatch.known.size,
      check: byMatch.check.size,
      rowsNew: rows.filter((l) => matchOf(l) === "new").length,
      rowsKnown: rows.filter((l) => matchOf(l) === "known").length,
      rowsCheck: rows.filter((l) => matchOf(l) === "check").length,
      warnings,
    };
  }, [rows]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((line) => {
      if (filter === "warnings" && !line.diagnostics.length) return false;
      if (filter !== "all" && filter !== "warnings" && matchOf(line) !== filter) return false;
      if (!q) return true;
      return [line.organization_name, websiteOf(line.source_cells)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [rows, filter, query]);
  const pages = Math.max(1, Math.ceil(visible.length / PAGE));
  const shown = visible.slice(page * PAGE, page * PAGE + PAGE);

  const tabs: { key: Filter; label: string; count: number }[] = [
    { key: "all", label: "All rows", count: counts.all },
    { key: "new", label: "New companies", count: counts.rowsNew },
    { key: "known", label: "Known", count: counts.rowsKnown },
    { key: "check", label: "To check", count: counts.rowsCheck },
    { key: "warnings", label: "Warnings", count: counts.warnings },
  ];
  const expired = new Date(receipt.expires_at).getTime() < Date.now();

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-900">{receipt.filename}</p>
            <p className="flex items-center gap-1 text-xs text-slate-500">
              <Clock className="h-3 w-3" />
              {expired
                ? "This preview has expired: upload the file again."
                : `Preview valid until ${formatDate(receipt.expires_at, "MMM d, HH:mm")} · business date ${formatDate(receipt.as_of)}`}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat
            icon={Rows3}
            label="Rows"
            value={receipt.counters.rows ?? 0}
            hint={
              receipt.counters.duplicate_rows
                ? `${receipt.counters.duplicate_rows} duplicate ${receipt.counters.duplicate_rows === 1 ? "row" : "rows"}`
                : "No duplicate row"
            }
          />
          <Stat
            icon={Building2}
            label="Companies"
            value={receipt.counters.companies ?? 0}
            hint={
              lines
                ? `${counts.newCompanies} new · ${counts.known} known · ${counts.check} to check`
                : "Reading the rows…"
            }
          />
          <Stat icon={Layers} label="Rounds" value={receipt.counters.rounds ?? 0} />
          <Stat
            icon={AlertTriangle}
            label="Warnings"
            value={lines ? counts.warnings : "…"}
            hint={counts.warnings ? "Rows with an invalid or missing value" : "Every row reads cleanly"}
            tone={counts.warnings ? "amber" : "slate"}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1" role="tablist">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={filter === tab.key}
                onClick={() => {
                  setFilter(tab.key);
                  setPage(0);
                }}
                className={cn(
                  "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                  filter === tab.key
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700",
                )}
              >
                {tab.label}
                <span className="ml-1 tabular-nums text-slate-400">{tab.count}</span>
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-56">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              placeholder="Search a company…"
              className="h-8 pl-8 text-sm"
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200">
          <div className="max-h-[42vh] overflow-auto">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Company</th>
                  <th className="px-3 py-2 font-medium">Round</th>
                  <th className="px-3 py-2 font-medium">Announced</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                  <th className="px-3 py-2 font-medium">Match</th>
                  <th className="px-3 py-2 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {!lines && (
                  <tr>
                    <td colSpan={6} className="px-3 py-10 text-center text-slate-400">
                      <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
                      Reading the rows…
                    </td>
                  </tr>
                )}
                {lines && shown.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-10 text-center text-slate-400">
                      No row matches.
                    </td>
                  </tr>
                )}
                {shown.map((line) => {
                  const match = MATCH[matchOf(line)];
                  const cells = line.source_cells;
                  return (
                    <tr key={line.line} className="align-top hover:bg-slate-50/60">
                      <td className="px-3 py-2">
                        <p className="font-medium text-slate-900">
                          {line.organization_name ?? "—"}
                        </p>
                        <p className="text-xs text-slate-500">
                          {websiteOf(cells) ?? `Row ${line.line}`}
                        </p>
                      </td>
                      <td className="px-3 py-2">
                        <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
                          {cells["Funding Type"] || "—"}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                        {cells["Announced Date"] ? formatDate(cells["Announced Date"]) : "—"}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-700">
                        {usd(cells["Money Raised (in USD)"]) ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          title={reasonLabel(line.result.reason)}
                          className={cn(
                            "whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
                            match.className,
                          )}
                        >
                          {match.label}
                        </span>
                        {matchOf(line) === "check" && (
                          <p className="mt-1 max-w-[220px] text-[11px] text-amber-700">
                            {reasonLabel(line.result.reason)}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex max-w-[240px] flex-wrap gap-1">
                          {line.diagnostics.map((d, i) => (
                            <span
                              key={`${d.code}-${i}`}
                              className="rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-800"
                            >
                              {diagnosticLabel(d)}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 bg-white px-3 py-2 text-xs text-slate-500">
            <span>
              {visible.length
                ? `${page * PAGE + 1}–${Math.min(visible.length, page * PAGE + PAGE)} of ${visible.length} rows`
                : "0 rows"}
            </span>
            <span className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2"
                disabled={page + 1 >= pages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </span>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            className="flex gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-6 py-4">
        <Button variant="ghost" disabled={busy} onClick={onBack}>
          <ArrowLeft />
          Choose another file
        </Button>
        <div className="flex items-center gap-3">
          <p className="hidden max-w-xs text-right text-[11px] text-slate-500 md:block">
            {canConfirm
              ? "The rounds are recorded, then the companies are re-scored. Nothing is sent to anyone."
              : "Confirming needs the rights to import companies and run the engine."}
          </p>
          <Button
            variant="primary"
            disabled={!canConfirm || busy || expired || !lines}
            onClick={onConfirm}
          >
            {busy ? <Loader2 className="animate-spin" /> : <Upload />}
            {busy ? "Starting…" : `Import ${receipt.counters.rounds ?? 0} rounds`}
          </Button>
        </div>
      </div>
    </div>
  );
}
